/**
 * siweNonce.js — Fetch dédupliqué du challenge SIWE (GET /auth/nonce?address=).
 *
 * PROBLÈME RÉSOLU (race nonce — backend logs 2026-06-15) :
 *   Plusieurs initiateurs SIWE peuvent démarrer quasi-simultanément pour le MÊME wallet :
 *     - le handler global accountsChanged (index.tsx → login())
 *     - une ou plusieurs instances <EarniWalletModal> (header.jsx ET wallet.jsx) → connectWallet()
 *     - wagmi/AppKit
 *   Chaque flux appelle GET /auth/nonce. Côté serveur, LoginNonces a `wallet` en PRIMARY KEY :
 *   `LoginNonce.upsert` ÉCRASE donc le nonce précédent. Le 2e (ou 6e) appel écrase le 1er →
 *   le message signé par le 1er flux ne correspond plus au nonce stocké → verify-signature
 *   répond 400 "invalid SIWE message format" → l'utilisateur N'EST PAS reconnecté.
 *   (Confirmé par les logs : 1 seul getNonce → verify 200 ; 2–6 getNonce concurrents → verify 400.)
 *
 * SOLUTION (§9 DRY, §26 — sans migration) :
 *   Un cache module-level (singleton, partagé par TOUS les initiateurs d'un même onglet) renvoie
 *   la MÊME promesse de nonce pour un wallet donné pendant une courte fenêtre. Conséquence :
 *   tous les flux concurrents reçoivent le MÊME message → un seul nonce stocké côté serveur →
 *   aucun écrasement → le message signé correspond toujours → verify-signature réussit.
 *
 *   La fenêtre est courte (DEDUP_WINDOW_MS) : elle couvre la rafale concurrente (mesurée 22–600 ms
 *   dans les logs) sans gêner une reconnexion manuelle ultérieure (> fenêtre → nonce frais).
 *
 * Source unique : authenticate.jsx (login global) ET WalletAuthService.js (modal) passent par ici.
 */
import { axiosWithoutEncryption } from './apiCallWithToken';

// Fenêtre de déduplication : assez longue pour absorber la rafale concurrente (les initiateurs
// partent à quelques dizaines/centaines de ms d'écart), assez courte pour qu'une nouvelle
// tentative manuelle (l'utilisateur re-clique après un échec, > 2 s) obtienne un nonce frais.
const DEDUP_WINDOW_MS = 2500;

// CACHE PARTAGÉ À L'ÉCHELLE DE LA PAGE — porté par `window`, PAS par le module.
// Raison (logs prod 2026-06-15, après 1re version module-level) : 6 GET /auth/nonce concurrents
// frappaient quand même le backend. Cause : webpack DUPLIQUE ce module dans plusieurs chunks
// (le bundle modal <EarniWalletModal> est lazy-loadé séparément du chunk principal). Chaque copie
// du module avait son PROPRE `new Map()` → aucune déduplication entre le handler global (chunk main)
// et les instances de modal (chunk lazy). Un cache porté par `window` est UNIQUE par page, partagé
// par TOUS les chunks/copies de module → la dédup fonctionne quel que soit le découpage webpack.
// Fallback module-level uniquement hors navigateur (tests Node : pas de window).
let _moduleFallback = null;
function getInflightCache() {
  if (typeof window !== 'undefined') {
    if (!window.__earniSiweNonceInflight) window.__earniSiweNonceInflight = new Map();
    return window.__earniSiweNonceInflight;
  }
  if (!_moduleFallback) _moduleFallback = new Map();
  return _moduleFallback;
}

/**
 * fetchSiweNonce — GET /auth/nonce?address=<address>, dédupliqué par adresse.
 * Retourne la réponse complète { nonce, message, expiresAt } (message = challenge EIP-4361).
 *
 * @param {string} serverBase — base API (process.env.REACT_APP_SERVER)
 * @param {string} address    — adresse wallet (0x…)
 * @returns {Promise<{nonce:string, message:string, expiresAt:string}>}
 */
export function fetchSiweNonce(serverBase, address) {
  const _inflight = getInflightCache();
  const key = String(address || '').toLowerCase();
  const now = Date.now();

  const cached = _inflight.get(key);
  if (cached && (now - cached.ts) < DEDUP_WINDOW_MS) {
    // Un flux concurrent a déjà demandé un nonce pour ce wallet → on partage SA promesse.
    return cached.promise;
  }

  const promise = axiosWithoutEncryption(
    `${serverBase}/auth/nonce?address=${encodeURIComponent(address)}`,
    'get',
    {}
  );

  _inflight.set(key, { promise, ts: now });

  // Éviction après la fenêtre (best-effort). On garde l'entrée le temps de la fenêtre même
  // après résolution, pour dédupliquer les initiateurs qui démarrent juste après la réponse.
  const evict = () => {
    const cur = _inflight.get(key);
    if (cur && cur.promise === promise && (Date.now() - cur.ts) >= DEDUP_WINDOW_MS) {
      _inflight.delete(key);
    }
  };
  promise.then(
    () => {
      const t = setTimeout(evict, DEDUP_WINDOW_MS);
      // Node (tests) : ne pas bloquer la sortie du process. No-op dans le navigateur (pas d'unref).
      if (t && typeof t.unref === 'function') t.unref();
    },
    () => { _inflight.delete(key); } // échec → ne pas mettre en cache une promesse rejetée
  );

  return promise;
}

// Exposé pour les tests (réinitialise le cache entre les cas).
export function __resetSiweNonceCacheForTests() {
  if (typeof window !== 'undefined' && window.__earniSiweNonceInflight) {
    window.__earniSiweNonceInflight.clear();
  }
  if (_moduleFallback) _moduleFallback.clear();
}
